import { Spinner } from '@/components/glitch-ui/spinner';
import { useState } from 'react';
import { useAtomValue } from 'jotai';
import { Settings, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { GoogleAdAccountSelectionModal } from '@/components/glitch-ui/google-ad-account-selection-modal';
import { googleAdsAccountResolvedAtom } from '@/store';

interface NoGoogleAdsAccountSelectedProps {
  description?: string;
}

export function NoGoogleAdsAccountSelected({
  description = 'A Google Ads account needs to be selected before this feature can be used.',
}: NoGoogleAdsAccountSelectedProps) {
  const [showAccountModal, setShowAccountModal] = useState(false);
  const accountResolved = useAtomValue(googleAdsAccountResolvedAtom);

  // On first paint the account atom is still hydrating (AppShell populates it in an
  // effect) — a loading state here stops every Google-data surface from flashing the
  // no-account placeholder while the app boots (#5820).
  if (!accountResolved) {
    return (
      <div className="flex items-center justify-center gap-3 min-h-[50vh]">
        <Spinner size="md" className="text-gray-500" />
        <span className="text-gray-500">Loading account...</span>
      </div>
    );
  }

  return (
    <>
      {/* Inline ``min-h-[50vh]`` empty state — same pattern as Landing Pages / Media
          / Conversions empty states on /assets. No nested Card wrapper. */}
      <div className="flex flex-col items-center justify-center min-h-[50vh] text-center">
        <Settings className="h-12 w-12 text-gray-500 mb-4" strokeWidth={1} />
        <h3 className="text-lg font-semibold text-gray-500 mb-2">No Google Ads Account Selected</h3>
        <p className="text-gray-500 mb-6">{description}</p>
        <Button
          onClick={() => setShowAccountModal(true)}
          size="lg"
          className="w-[280px] py-6 bg-[#290BD9] hover:bg-[#2309C5] text-white shadow-lg flex items-center justify-center space-x-2"
        >
          <span>SELECT ACCOUNT</span>
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
      <GoogleAdAccountSelectionModal isOpen={showAccountModal} onClose={() => setShowAccountModal(false)} />
    </>
  );
}
